import { ref, reactive, computed } from 'vue'
import type { ContextMenuState } from '../types/verseSelection'

const MENU_MARGIN = 8
const EDGE_THRESHOLD = 32
const SCROLL_SPEED = 8

export function clampMenuPosition(
  x: number,
  y: number,
  frameWidth: number,
  frameHeight: number,
  menuWidth: number,
  menuHeight: number,
): { x: number; y: number } {
  const maxX = frameWidth - menuWidth - MENU_MARGIN
  const maxY = frameHeight - menuHeight - MENU_MARGIN
  return {
    x: Math.max(MENU_MARGIN, Math.min(x, maxX)),
    y: Math.max(MENU_MARGIN, Math.min(y, maxY)),
  }
}

export function versesInRange(
  anchor: number,
  target: number,
  allVerses: number[],
): number[] {
  const lo = Math.min(anchor, target)
  const hi = Math.max(anchor, target)
  return allVerses.filter(v => v >= lo && v <= hi).sort((a, b) => a - b)
}

export function computeAutoScroll(
  mouseY: number,
  contentRect: { top: number; bottom: number },
): number {
  if (mouseY < contentRect.top + EDGE_THRESHOLD) return -SCROLL_SPEED
  if (mouseY > contentRect.bottom - EDGE_THRESHOLD) return SCROLL_SPEED
  return 0
}

export function useVerseSelection() {
  const selectedVerses = ref(new Set<number>())
  const anchorVerse = ref<number | null>(null)
  const isDragging = ref(false)
  const isCtrlPressed = ref(false)

  const contextMenu = reactive<ContextMenuState>({
    visible: false,
    x: 0,
    y: 0,
  })

  let autoScrollRafId: number | null = null

  const isSelected = (verse: number): boolean => selectedVerses.value.has(verse)

  const selectSingle = (verse: number): void => {
    selectedVerses.value = new Set([verse])
    anchorVerse.value = verse
  }

  const toggleVerse = (verse: number): void => {
    const newSet = new Set(selectedVerses.value)
    if (newSet.has(verse)) {
      newSet.delete(verse)
    } else {
      newSet.add(verse)
    }
    selectedVerses.value = newSet
  }

  const startDrag = (verse: number, ctrlKey: boolean): void => {
    isDragging.value = true
    anchorVerse.value = verse

    if (!ctrlKey) {
      selectedVerses.value = new Set([verse])
    } else {
      const newSet = new Set(selectedVerses.value)
      newSet.add(verse)
      selectedVerses.value = newSet
    }
  }

  const extendDrag = (verse: number, allVerses: number[]): void => {
    if (anchorVerse.value === null) return

    const rangeVerses = versesInRange(anchorVerse.value, verse, allVerses)

    if (isCtrlPressed.value) {
      const baseSet = new Set(selectedVerses.value)
      rangeVerses.forEach(v => baseSet.add(v))
      selectedVerses.value = baseSet
    } else {
      selectedVerses.value = new Set(rangeVerses)
    }
  }

  const endDrag = (): void => {
    isDragging.value = false
    stopAutoScroll()
  }

  const showContextMenu = (x: number, y: number): void => {
    contextMenu.visible = true
    contextMenu.x = x
    contextMenu.y = y
  }

  const hideContextMenu = (): void => {
    contextMenu.visible = false
  }

  const clearSelection = (): void => {
    selectedVerses.value = new Set()
    anchorVerse.value = null
    isDragging.value = false
    hideContextMenu()
    stopAutoScroll()
  }

  const startAutoScroll = (
    contentEl: HTMLElement,
    mouseY: number,
  ): void => {
    const loop = (): void => {
      const rect = contentEl.getBoundingClientRect()
      const delta = computeAutoScroll(mouseY, {
        top: rect.top,
        bottom: rect.bottom,
      })

      if (delta !== 0) {
        contentEl.scrollTop += delta
        autoScrollRafId = requestAnimationFrame(loop)
      } else {
        autoScrollRafId = null
      }
    }

    if (autoScrollRafId === null) {
      autoScrollRafId = requestAnimationFrame(loop)
    }
  }

  const stopAutoScroll = (): void => {
    if (autoScrollRafId !== null) {
      cancelAnimationFrame(autoScrollRafId)
      autoScrollRafId = null
    }
  }

  const setCtrlPressed = (pressed: boolean): void => {
    isCtrlPressed.value = pressed
  }

  return {
    selectedVerses: computed(() => selectedVerses.value),
    anchorVerse: computed(() => anchorVerse.value),
    isDragging: computed(() => isDragging.value),
    isCtrlPressed: computed(() => isCtrlPressed.value),
    contextMenu,
    isSelected,
    selectSingle,
    toggleVerse,
    startDrag,
    extendDrag,
    endDrag,
    showContextMenu,
    hideContextMenu,
    clearSelection,
    startAutoScroll,
    stopAutoScroll,
    setCtrlPressed,
  }
}
